/**
 * In-app browser detection, for the few places where one of them needs
 * different markup rather than just different styling.
 *
 * TikTok's in-app browser (BytedanceWebview, on iOS and Android) treats every
 * <video> on the page as a player: it draws its own chrome over the element
 * (fullscreen / picture-in-picture buttons, a mute button, a scrubber with a
 * timer, and a big play button with ±10s skips when the video isn't playing),
 * greys out the frame, and sends the video fullscreen when it mounts or is
 * tapped. None of that can be styled away from the page — it's native UI —
 * and `playsinline`/`muted`/no `controls` make no difference. So decorative
 * videos (the funnel's sky, the loading panda, the mascot clips) are rendered
 * as animated images there instead (NoControlsVideo's `animated`).
 *
 * TikTok is not one browser. Links from ads open in the main in-app browser,
 * whose user agent carries TikTok's own tokens; links from business chat open
 * in a container whose user agent looked like a bare WKWebView (no tokens —
 * the first token-only build didn't switch over there). So the switch is:
 * TikTok's tokens, OR any embedded WebView at all. Other apps' in-app
 * browsers (Instagram, Facebook, Messenger…) get the image path too; it
 * costs a little bandwidth there and nothing else.
 */

// "musical_ly" is TikTok's app name in both the iOS and Android user agents;
// "BytedanceWebview" is the in-app browser itself.
const TIKTOK_UA = /BytedanceWebview|musical_ly|\bTikTok\b/i;

const currentUa = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent || '');

export const isTikTokInAppBrowser = (ua = currentUa()) => TIKTOK_UA.test(ua);

// Every real browser on iOS — Safari, Chrome, Firefox, Edge, Brave, DuckDuckGo —
// carries a "Safari/" token; an app's embedded WKWebView does not. An iPad
// WebView says "Macintosh" like iPad Safari does, hence the touch check.
const isIosWebView = (ua) => {
  const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  const apple = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && touch);
  return apple && /AppleWebKit/.test(ua) && !/Safari\//.test(ua);
};

// Android's WebView marks itself with "; wv)" and keeps the old "Version/x.y"
// token that Chrome proper dropped.
const isAndroidWebView = (ua) =>
  /Android/.test(ua) && (/;\s*wv\)/.test(ua) || /Version\/\d+\.\d+.*Chrome\//.test(ua));

/** True inside TikTok, or inside any other app's embedded browser. */
export const isEmbeddedWebView = (ua = currentUa()) =>
  isTikTokInAppBrowser(ua) || isIosWebView(ua) || isAndroidWebView(ua);
